import { Component, h, Prop, State, Event, EventEmitter } from '@stencil/core';
import { Constants, DateUtils, FeedItemDoc, FeedItemSubType, FileDoc, PersonDoc } from 'common';
import { CommentSubmit } from '../../global/models/CommentSubmit';
import { FeedService } from 'src/global/service/FeedService';
import { User } from '../../global/models';
import { FeedFilter } from 'src/global';

@Component({
  tag: 'rfi-comments-card',
  styleUrls: ['../../global/styles/workflow-view.css'],
})
export class RfiCommentsCard {
  @Prop() projectId: string;
  @Prop() rfiId: string;
  @Prop() people: PersonDoc[] = [];
  @Prop() files: FileDoc[] = [];
  @Prop() feed: FeedItemDoc[] = [];
  @Prop() canWrite: boolean = false;
  @Prop() isMobile: boolean = false;
  @Prop() activeFilter: FeedFilter = FeedFilter.ALL;
  @Prop() newComment: string = '';
  @Prop() acceptedAnswers: string[] = [];
  @Prop() isApprovingStep: boolean = false;

  @Event() setFilter: EventEmitter<FeedFilter>;
  @Event() commentSubmit: EventEmitter<CommentSubmit>;
  @Event() newCommentChange: EventEmitter<string>;
  @Event() feedSelectedChange: EventEmitter<FeedItemDoc[]>;
  @Event() setCommentsRef: EventEmitter<HTMLDivElement>;
  @Event() setAcceptedAnswerClick: EventEmitter<{ answerId: string; text: string }>;

  @State() feedSelected: FeedItemDoc[] = [];

  private commentsRef: HTMLDivElement;

  get user(): User {
    return globalThis.user;
  }

  getFilterMapping() {
    return {
      'all': null,
      'comments': [FeedItemSubType.COMMENT, FeedItemSubType.EDIT_COMMENT],
      'files': [FeedItemSubType.FILE, FeedItemSubType.PHOTO, FeedItemSubType.DRAWING, FeedItemSubType.SPECIFICATION],
      'status-history': [FeedItemSubType.STATUS, FeedItemSubType.PROGRESS, FeedItemSubType.APPROVE, FeedItemSubType.REJECT, FeedItemSubType.PROPOSE],
      'accepted-answers': 'accepted-answers',
    };
  }

  getFilteredFeed(): FeedItemDoc[] {
    if (this.activeFilter === 'all') {
      return this.feed;
    }

    const nonDeletedFeed = this.feed.filter(item => !item.deleted_by);
    const filterMapping = this.getFilterMapping();
    const allowedSubTypes = filterMapping[this.activeFilter];

    if (!allowedSubTypes) {
      return nonDeletedFeed;
    }

    return nonDeletedFeed.filter(item => allowedSubTypes.includes(item.sub));
  }

  componentDidLoad() {
    if (this.commentsRef) {
      this.setCommentsRef.emit(this.commentsRef);
    }
  }

  getFeedItemButton(item: FeedItemDoc) {
    if (!this.canWrite || item.sub !== FeedItemSubType.FILE) return null;

    const fileId = item.sub_id;
    const isAccepted = this.acceptedAnswers?.includes(fileId);

    return <feed-item-accept-button isAccepted={isAccepted} />;
  }

  async deleteFeedItem(item: FeedItemDoc) {
    const updatedItem = { ...item, deleted_by: this.user.id, deleted_date: DateUtils.currentIso };
    await FeedService.storeFeedItem(this.projectId, updatedItem);
  }

  render() {
    const filteredFeed = this.getFilteredFeed();
    return (
      <div class="ion-display-flex ion-flex-column" style={{ height: '100%' }}>
        <feed-filter-buttons
          activeFilter={this.activeFilter}
          acceptedAnswersCount={this.acceptedAnswers?.length || 0}
          isMobile={this.isMobile}
          onFilterChange={e => this.setFilter.emit(e.detail)}
        />
        <div class="flex-grow cc-scrollbars workflow-view-detail-feed-container ion-padding-horizontal" ref={el => (this.commentsRef = el as HTMLDivElement)}>
          {filteredFeed.length === 0 ? (
            <p>No items found for the selected filter.</p>
          ) : (
            <concourse-feed
              projectId={this.projectId}
              people={this.people}
              files={this.files}
              feed={filteredFeed}
              onItemsSelected={e => {
                this.feedSelected = e.detail as FeedItemDoc[];
                this.feedSelectedChange.emit(this.feedSelected);
              }}
              buttonCallback={this.isMobile ? null : this.getFeedItemButton.bind(this)}
            />
          )}
        </div>
        {this.canWrite && (
          <div class="ion-padding-horizontal ion-padding-bottom">
            {this.feedSelected.length > 0 ? (
              <div class="ion-display-flex ion-align-items-center" style={{ height: '50px' }}>
                <span style={{ color: '#666', flex: '1' }}>{this.feedSelected.length} comments selected</span>
                <ion-button fill="clear" onClick={() => console.log(`Delete selected items: ${this.feedSelected.map(item => item._id).join(', ')}`)}>
                  <ion-icon slot="icon-only" name="trash-outline"></ion-icon>
                </ion-button>
              </div>
            ) : (
              <concourse-comment
                projectId={this.projectId}
                feature="rfis"
                featureId={this.rfiId}
                placeholder="Enter comment"
                rows={`${this.isMobile ? 1 : 3}`}
                isMobile={this.isMobile}
                value={this.newComment}
                fileRoles={[`p-${this.projectId}-rfis-${this.rfiId}-read`, `p-${this.projectId}-rfis-${this.rfiId}-write`]}
                onMessageChange={(e: CustomEvent) => this.newCommentChange.emit(e.detail)}
                onCommentSubmit={(e: CustomEvent<CommentSubmit>) => this.commentSubmit.emit(e.detail)}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}
